import { useEffect, useState } from "react";
import {
  Plus, Pencil, Copy, Trash2, Star, Eye, EyeOff, Loader2, Search,
} from "lucide-react";
import api from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { formatPrice, typeLabel, statusColor, PROPERTY_TYPES, img } from "../../lib/utils-app";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { ImageUploader } from "../../components/ImageUploader";

const empty = {
  title: { en: "", es: "" }, description: { en: "", es: "" },
  transaction_type: "sale", property_type: "house", price: 0, currency: "USD",
  city: "", neighborhood: "", address: "", zip: "", bedrooms: 0, bathrooms: 0,
  parking: 0, sqft: 0, lot_size: 0, year_built: 2024, status: "active", featured: false,
  amenities: [], features: [], images: [], virtual_tour_url: "",
};

export function ListingManager() {
  const { t, lang, L } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/properties?limit=100");
    setItems(data.items);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...empty }); setOpen(true); };
  const openEdit = (p) => {
    setEditing({ ...empty, ...p, amenities: p.amenities || [], images: p.images || [] });
    setOpen(true);
  };

  const toggleStatus = async (p, field) => {
    const val = field === "featured" ? !p.featured : (p.status === "active" ? "draft" : "active");
    await api.patch(`/properties/${p.id}/status`, { [field]: val });
    toast.success("Updated");
    load();
  };

  const duplicate = async (p) => { await api.post(`/properties/${p.id}/duplicate`); toast.success("Duplicated"); load(); };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${L(p.title)}"?`)) return;
    await api.delete(`/properties/${p.id}`); toast.success("Deleted"); load();
  };

  const filtered = items.filter((p) => !q || L(p.title).toLowerCase().includes(q.toLowerCase()) || p.city.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-4xl font-light">{t("dash.listings")}</h1>
          <p className="text-muted-foreground font-light mt-1">{items.length} properties</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground label-caps hover:brightness-110 transition-[filter]" data-testid="add-listing-btn">
          <Plus size={16} /> {t("dash.addListing")}
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4 max-w-sm border border-border px-3">
        <Search size={16} className="text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} className="flex-1 bg-transparent py-2.5 text-sm outline-none" data-testid="listing-search" />
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="listings-table">
            <thead>
              <tr className="border-b border-border text-left label-caps text-muted-foreground text-[0.6rem]">
                <th className="p-4">Property</th>
                <th className="p-4 hidden md:table-cell">Type</th>
                <th className="p-4">Price</th>
                <th className="p-4 hidden lg:table-cell">Status</th>
                <th className="p-4 hidden lg:table-cell">Views</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`listing-row-${p.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img src={img(p.images?.[0])} alt="" className="w-12 h-12 object-cover flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-light truncate max-w-[200px]">{L(p.title)}</p>
                        <p className="text-xs text-muted-foreground">{p.city}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell capitalize text-muted-foreground">{typeLabel(p.property_type, lang)}</td>
                  <td className="p-4 text-primary">{formatPrice(p.price)}</td>
                  <td className="p-4 hidden lg:table-cell"><span className={`text-xs px-2 py-0.5 border capitalize ${statusColor(p.status)}`}>{p.status}</span></td>
                  <td className="p-4 hidden lg:table-cell text-muted-foreground">{p.views}</td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => toggleStatus(p, "featured")} title="Feature" className={`p-2 hover:text-primary transition-colors ${p.featured ? "text-primary" : "text-muted-foreground"}`} data-testid={`feature-${p.id}`}><Star size={15} className={p.featured ? "fill-primary" : ""} /></button>
                      <button onClick={() => toggleStatus(p, "status")} title="Publish" className="p-2 text-muted-foreground hover:text-primary transition-colors" data-testid={`publish-${p.id}`}>{p.status === "active" ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                      <button onClick={() => openEdit(p)} className="p-2 text-muted-foreground hover:text-primary transition-colors" data-testid={`edit-${p.id}`}><Pencil size={15} /></button>
                      <button onClick={() => duplicate(p)} className="p-2 text-muted-foreground hover:text-primary transition-colors" data-testid={`duplicate-${p.id}`}><Copy size={15} /></button>
                      <button onClick={() => remove(p)} className="p-2 text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-${p.id}`}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ListingForm open={open} setOpen={setOpen} editing={editing} onSaved={load} />
    </div>
  );
}

function ListingForm({ open, setOpen, editing, onSaved }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (editing) setForm(editing); }, [editing]);
  if (!editing) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (parent, k, v) => setForm((f) => ({ ...f, [parent]: { ...f[parent], [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price), bedrooms: Number(form.bedrooms), bathrooms: Number(form.bathrooms),
      parking: Number(form.parking), sqft: Number(form.sqft), lot_size: Number(form.lot_size),
      year_built: Number(form.year_built) || null,
      amenities: Array.isArray(form.amenities) ? form.amenities : String(form.amenities).split(",").map((s) => s.trim()).filter(Boolean),
      images: Array.isArray(form.images) ? form.images : String(form.images).split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (form.id) await api.put(`/properties/${form.id}`, payload);
      else await api.post("/properties", payload);
      toast.success(form.id ? "Updated" : "Created");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error("Error saving"); } finally { setSaving(false); }
  };

  const inp = "w-full mt-1.5 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none transition-colors";
  const lbl = "label-caps text-muted-foreground text-[0.6rem]";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{form.id ? "Edit" : "New"} Listing</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="listing-form">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Title (EN)</label><input required value={form.title.en} onChange={(e) => setNested("title", "en", e.target.value)} className={inp} data-testid="form-title-en" /></div>
            <div><label className={lbl}>Title (ES)</label><input value={form.title.es} onChange={(e) => setNested("title", "es", e.target.value)} className={inp} data-testid="form-title-es" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Description (EN)</label><textarea rows={2} value={form.description.en} onChange={(e) => setNested("description", "en", e.target.value)} className={inp + " resize-none"} /></div>
            <div><label className={lbl}>Description (ES)</label><textarea rows={2} value={form.description.es} onChange={(e) => setNested("description", "es", e.target.value)} className={inp + " resize-none"} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={lbl}>Transaction</label>
              <Select value={form.transaction_type} onValueChange={(v) => set("transaction_type", v)}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="form-transaction"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sale">Sale</SelectItem><SelectItem value="rent">Rent</SelectItem></SelectContent>
              </Select>
            </div>
            <div><label className={lbl}>Type</label>
              <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{PROPERTY_TYPES.map((pt) => <SelectItem key={pt} value={pt}>{typeLabel(pt, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className={lbl}>Price</label><input type="number" required value={form.price} onChange={(e) => set("price", e.target.value)} className={inp} data-testid="form-price" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={lbl}>City</label><input value={form.city} onChange={(e) => set("city", e.target.value)} className={inp} data-testid="form-city" /></div>
            <div><label className={lbl}>Neighborhood</label><input value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>ZIP</label><input value={form.zip} onChange={(e) => set("zip", e.target.value)} className={inp} /></div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div><label className={lbl}>Beds</label><input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Baths</label><input type="number" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Parking</label><input type="number" value={form.parking} onChange={(e) => set("parking", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Sqft</label><input type="number" value={form.sqft} onChange={(e) => set("sqft", e.target.value)} className={inp} /></div>
          </div>
          <div><label className={lbl}>Images & Media</label><div className="mt-1.5"><ImageUploader value={Array.isArray(form.images) ? form.images : String(form.images || "").split(",").map((s) => s.trim()).filter(Boolean)} onChange={(imgs) => set("images", imgs)} lang={lang} /></div></div>
          <div><label className={lbl}>Amenities (comma separated)</label><input value={Array.isArray(form.amenities) ? form.amenities.join(", ") : form.amenities} onChange={(e) => set("amenities", e.target.value)} className={inp} /></div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div><label className={lbl}>Status</label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem><SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem><SelectItem value="rented">Rented</SelectItem><SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-3 py-2.5"><Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="form-featured" /> <span className="text-sm">Featured</span></label>
          </div>
          <button disabled={saving} className="w-full py-3 bg-primary text-primary-foreground label-caps disabled:opacity-50" data-testid="form-save">{saving ? t("common.sending") : t("common.save")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
